<template>
  <Page class="customer-show">
    <Header ref="customerHeader" @fetch-user="reloadCustomerInfo" />
    <TabSwitcher
      id="c"
      class="tab-switcher mt-8"
      :tabs="tabs"
      :ready="tabsReady"
    />
    <ImportedDataWarning />
  </Page>
</template>

<script>
import { mapActions, mapGetters, mapState } from "vuex";
import { EventBus } from "@/lib/event_bus.js";
import { getQueryVariables, updateQueryParam } from "@/lib/url";
import { useAccessRole } from "@/lib/useAccessRole";

import ImportedDataWarning from "@/components/ImportedDataWarning.vue";
import Page from "@/components/Page.vue";
import TabSwitcher from "@/components/TabSwitcher/TabSwitcher.vue";
import SubCustomersTab from "@/Customer/Profile/SubCustomersTab.vue";
import useVuelidate from "@vuelidate/core";
import CustomerAddressTab from "./CustomerAddressesTab.vue";
import CustomerCallsTab from "./CustomerCallsTab.vue";
import CustomerCommunicationsTab from "./CustomerCommunicationsTab.vue";
import CustomerEquipmentTab from "./CustomerEquipmentTab.vue";
import CustomerInvoicesTab from "./CustomerInvoicesTab.vue";
import CustomerMediaTab from "./CustomerMediaTab.vue";
import CustomerMembershipTab from "./CustomerMembershipTab.vue";
import CustomerNotesTab from "./CustomerNotesTab.vue";
import CustomerQuotesTab from "./CustomerQuotesTab.vue";
import CustomerServiceHistoryTab from "./CustomerServiceHistoryTab.vue";
import Header from "./Header.vue";

export default {
  components: {
    Page,
    Header,
    TabSwitcher,
    ImportedDataWarning
  },
  props: {
    customerId: { type: [Number, String], default: null },
    invoiceDatatableSource: { type: String, default: "" },
    quoteDatatableSource: { type: String, default: "" },
    quoteFilters: { type: Array, default: () => [] },
    appointmentsDatatableSource: { type: String, default: "" },
    appointmentFilters: { type: Array, default: () => [] },
    subCustomersDatatableSource: { type: String, default: "" },
    subCustomerFilters: { type: Array, default: () => [] },
    invoiceFilters: { type: Array, default: () => [] },
    phoneCallFilters: { type: Array, default: () => [] },
    communicationSettingsPath: { type: String, default: "" },
    communicationsChangeHistoryPath: { type: String, default: "" }
  },
  setup() {
    const { canAccess, accessResolved } = useAccessRole();
    return {
      canAccess,
      accessResolved,
      v$: useVuelidate()
    };
  },
  computed: {
    ...mapGetters(["parentSubEnabled"]),
    ...mapState(["currentUser", "paymentMethods", "tenant", "fetchingTenant"]),
    // The tab strip must not be clickable until its membership is final, or an
    // early click lands on a tab that then shifts position as gated tabs appear.
    // The set depends on canAccess() (accessResolved) and currentUser (whose
    // `parent` flag gates Sub-Customers), so require both before enabling it.
    tabsReady() {
      return this.accessResolved && Boolean(this.currentUser);
    },
    tabs({ tenant }) {
      const tabs = [
        {
          name: "Addresses",
          component: CustomerAddressTab,
          props: {
            source: this.appointmentsDatatableSource,
            filters: this.appointmentFilters,
            customerId: this.customerId
          }
        },
        {
          name: "Sub-Customers",
          component: SubCustomersTab,
          props: {
            source: this.subCustomersDatatableSource,
            filters: this.subCustomerFilters,
            customerId: this.customerId
          },
          visible:
            this.parentSubEnabled &&
            !this.currentUser?.parent &&
            this.canAccess("customers")
        },
        {
          name: "Service History",
          component: CustomerServiceHistoryTab,
          props: {
            source: this.appointmentsDatatableSource,
            filters: this.appointmentFilters,
            customerId: this.customerId
          },
          visible: this.canAccess("jobs")
        },
        {
          name: "Notes",
          component: CustomerNotesTab
        },
        {
          name: "Quotes",
          ref: "quotesTab",
          component: CustomerQuotesTab,
          props: {
            source: this.quoteDatatableSource,
            filters: this.quoteFilters
          },
          beforeTabChange: (ref) => {
            updateQueryParam("quote_id", null);
            ref?.setCurrentQuote(null);
            return true;
          },
          visible: this.canAccess("quoting")
        },
        {
          name: "Invoices",
          ref: "invoicesTab",
          component: CustomerInvoicesTab,
          props: {
            source: this.invoiceDatatableSource,
            filters: this.invoiceFilters
          },
          beforeTabChange: (ref) => {
            updateQueryParam("invoice_id", null);
            ref?.setCurrentInvoice(null);
            ref?.refreshInvoiceList?.();
            return true;
          },
          visible: this.canAccess("financial_data")
        },
        {
          name: "Media and Documents",
          component: CustomerMediaTab,
          props: {
            customerId: this.customerId
          },
          visible: this.canAccess("jobs")
        },
        { name: "Equipment", component: CustomerEquipmentTab },
        { name: "Membership", component: CustomerMembershipTab },
        {
          name: "Phone Calls",
          component: CustomerCallsTab,
          props: {
            filters: this.phoneCallFilters
          }
        },
        {
          name: "Communications",
          component: CustomerCommunicationsTab,
          ref: "communicationsTab",
          props: {
            settingsPath: this.communicationSettingsPath,
            changeHistoryPath: this.communicationsChangeHistoryPath
          },
          beforeTabChange: (ref) => {
            if (ref?.settingsChanged()) {
              return window.confirm("Changes you made may not be saved.");
            }
            return true;
          }
        }
      ];
      return tabs;
    }
  },
  watch: {
    currentUser(newValue, oldValue) {
      if (newValue !== oldValue && newValue) {
        this.$store.dispatch("addBreadcrumb", {
          label: this.fullName(newValue)
        });
      }
    },
    customerId() {
      this.reloadCustomerInfo();
    }
  },
  async mounted() {
    if (!this.tenant && !this.fetchingTenant) {
      await this.fetchTenant();
    }
    await this.fetchTenantCustomizations();
    await this.reloadCustomerInfo();
    if (this.paymentMethods && !this.paymentMethods.length) {
      this.fetchPaymentMethods();
    }
    this.fetchMembershipPrograms();
    this.checkQueryParamShortcuts();
    EventBus.$on("payment-method-updated", this.reloadCustomerInfo);
    EventBus.$on("payment-method-removed", this.reloadCustomerInfo);
  },
  beforeUnmount() {
    EventBus.$off("payment-method-updated", this.reloadCustomerInfo);
    EventBus.$off("payment-method-removed", this.reloadCustomerInfo);
    this.clearCurrentUser();
  },
  methods: {
    ...mapActions([
      "fetchUser",
      "fetchPaymentMethods",
      "fetchMembershipPrograms",
      "fetchTenantCustomizations",
      "fetchTenant",
      "fetchCustomerEquipment",
      "clearCurrentUser"
    ]),
    checkQueryParamShortcuts() {
      const queryVariables = getQueryVariables();

      // When we see the ?edit_customer, we will try and launch the edit customer modal
      // After we launch the modal, we remove the query parameter immediately
      if (queryVariables.edit_customer) {
        updateQueryParam("edit_customer", undefined);
        try {
          this.$refs.customerHeader.showEditModal();
        } catch (e) {
          console.error("Unable to launch customer edit modal");
        }
      }
    },
    reloadCustomerInfo() {
      return this.fetchUser({ id: this.customerId });
    },
    toggleModal(modal) {
      this[modal] = !this[modal];
    }
  }
};
</script>

<style lang="scss" scoped>
.tab-switcher:deep(> .card-body) {
  padding-left: 0;
  padding-right: 0;
}
</style>
